import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, iif, of } from 'rxjs';
import { map, mergeMap, withLatestFrom, catchError } from 'rxjs/operators';

/* notification service */
import { NotificationService } from 'src/app/features/shared/services/notification-service/notification.service';

/* jsonata object class */
import { JSONAtaObject } from 'src/app/features/shared/helper-classes';

/* lodash lib */
import * as lodash from 'lodash';

/* DataStorage interface for ngrx */
import { DataStorage } from 'src/app/features/data-stores/data-storage.interface';

/* navigate action */
import { Navigate } from 'src/app/features/data-stores/router-data-store/state/router-data-store.actions';

/* Workflow Management API Service */
import { WorkflowManagementControllerService } from 'src/app/features/shared/sdk/services/workflow-management-controller.service';

/* rooming-data-store actions */
import * as RoomingDataStoreActions from 'src/app/features/data-stores/rooming-data-store/state/rooming-data-store.actions';

/* api services */
import { RoomingControllerService } from 'src/app/features/shared/sdk_v1/services';

@Injectable()
export class RoomingDataStoreEffects {
  constructor(
    private actions$: Actions,
    private store: Store<DataStorage>,
    private notificationService: NotificationService,
    private workflowManagementApi: WorkflowManagementControllerService,
    private roomingApiService: RoomingControllerService,
  ) { 
    // prettier-ignore
    // #region customCode: data-store-effects__constructor__start
    
    // #endregion customCode: data-store-effects__constructor__start

    // prettier-ignore
    // #region customCode: data-store-effects__constructor__end
    
    // #endregion customCode: data-store-effects__constructor__end
  }

  getRoomings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomingDataStoreActions.GetRoomings),
      mergeMap((action) => {
        return this.roomingApiService.find().pipe(
          mergeMap((roomings) => {
            const payload: any = {
              roomings: roomings,
            };
            return [RoomingDataStoreActions.GetRoomingsSuccessful({ payload })];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while fetching data.',
            );
            return [RoomingDataStoreActions.GetRoomingsFailure()];
          }),
        );
      }),
    ),
  );

  getRoomingById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomingDataStoreActions.GetRoomingById),
      mergeMap((action) => {
        return this.roomingApiService
          .findById({
            id: action.payload.id,
          })
          .pipe(
            mergeMap((rooming) => {
              const payload: any = {
                rooming: rooming,
              };
              return [
                RoomingDataStoreActions.GetRoomingByIdSuccessful({ payload }),
              ];
            }),
            catchError((error) => {
              this.notificationService.createNotification(
                'error',
                'Failure',
                'There was an error while fetching data.',
              );
              return [RoomingDataStoreActions.GetRoomingByIdFailure()];
            }),
          );
      }),
    ),
  );

  getActiveRoomingWithRelationsById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomingDataStoreActions.GetActiveRoomingWithRelationsById),
      mergeMap((action) => {
        return this.roomingApiService
          .findById({
            id: action.payload.id,
          })
          .pipe(
            mergeMap((rooming) => {
              const payload: any = {
                activeRooming: rooming,
              };
              return [
                RoomingDataStoreActions.GetActiveRoomingByIdWithRelationsSuccessful(
                  { payload },
                ),
              ];
            }),
            catchError((error) => {
              this.notificationService.createNotification(
                'error',
                'Failure',
                'There was an error while fetching data.',
              );
              return [
                RoomingDataStoreActions.GetActiveRoomingByIdWithRelationsFailure(),
              ];
            }),
          );
      }),
    ),
  );

  createRooming$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomingDataStoreActions.CreateRooming),
      mergeMap((action) => {
        const params = {
          body: action.payload.rooming,
        };
        return this.roomingApiService.create(<any>params).pipe(
          mergeMap((rooming) => {
            this.notificationService.createNotification(
              'success',
              'Successful',
              'Your data has been saved successfully.',
            );
            const payload: any = {
              rooming: rooming,
            };
            return [
              RoomingDataStoreActions.CreateRoomingSuccessful({ payload }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while saving the data.',
            );
            return [RoomingDataStoreActions.CreateRoomingFailure()];
          }),
        );
      }),
    ),
  );

  updateRooming$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomingDataStoreActions.UpdateRooming),
      mergeMap((action) => {
        const params = {
          id: action.payload.id,
          body: action.payload.data,
        };
        return this.roomingApiService.updateById(<any>params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Veri güncellendi',
              'Verileriniz başarıyla güncellendi.',
            );
            return [RoomingDataStoreActions.GetRoomings()];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while updating the data.',
            );
            return [RoomingDataStoreActions.UpdateRoomingFailure()];
          }),
        );
      }),
    ),
  );

  deleteRooming$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomingDataStoreActions.DeleteRooming),
      mergeMap((action) => {
        const params = { id: action.payload.id };
        return this.roomingApiService.deleteById(params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Success',
              'Your data has been deleted successfully.',
            );
            const payload: any = {
              id: action.payload.id,
            };
            return [
              RoomingDataStoreActions.DeleteRoomingSuccessful({ payload }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while deleting the data.',
            );
            return [RoomingDataStoreActions.DeleteRoomingFailure()];
          }),
        );
      }),
    ),
  );
}
